import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import adminService from '../../services/adminService';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import './LocationManagement.css';

const LocationManagement = () => {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    loadLocations();
  }, [filterType]);

  const loadLocations = async () => {
    try {
      const params = {};
      if (filterType) params.type = filterType;
      
      const data = await adminService.getLocations(params);
      setLocations(data.data || []);
    } catch (error) {
      console.error('Failed to load locations:', error);
      alert('Failed to load locations');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (id) => {
    try {
      await adminService.toggleLocationActive(id);
      loadLocations();
    } catch (error) {
      console.error('Failed to toggle location:', error);
      alert('Failed to toggle location status');
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Are you sure you want to delete "${name}"?`)) {
      return;
    }

    try {
      await adminService.deleteLocation(id);
      alert('Location deleted successfully');
      loadLocations();
    } catch (error) {
      console.error('Failed to delete location:', error);
      alert('Failed to delete location');
    }
  };

  const filteredLocations = locations.filter(location =>
    location.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="location-management container">
        <p>Loading locations...</p>
      </div>
    );
  }

  return (
    <div className="location-management container">
      <div className="management-header">
        <h1>Location Management</h1>
        <div className="header-actions">
          <Button onClick={() => navigate('/admin/dashboard')} variant="secondary">
            ← Back to Dashboard
          </Button>
          <Button onClick={() => navigate('/admin/locations/new')} variant="primary">
            ➕ Add New Location
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="filters-card">
        <div className="filters">
          <Input
            type="text"
            placeholder="Search locations..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          
          <select 
            className="filter-select"
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
          >
            <option value="">All Types</option>
            <option value="city">City</option>
            <option value="village">Village</option>
            <option value="landmark">Landmark</option>
            <option value="highway">Highway</option>
            <option value="cultural">Cultural</option>
          </select>
        </div>
      </Card>

      {/* Locations Table */}
      <Card className="locations-table-card">
        <div className="table-header">
          <h3>Locations ({filteredLocations.length})</h3>
        </div>
        
        {filteredLocations.length === 0 ? (
          <p className="no-locations">No locations found. Add your first location!</p>
        ) : (
          <div className="locations-table">
            <table>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Name</th>
                  <th>Type</th>
                  <th>Province</th>
                  <th>Drop Points</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredLocations.map((location) => (
                  <tr key={location.id}>
                    <td>{location.id}</td>
                    <td className="location-name-cell">
                      <strong>{location.name}</strong>
                    </td>
                    <td>
                      <span className="type-badge">{location.type}</span>
                    </td>
                    <td>{location.province || '-'}</td>
                    <td>
                      <span className={`drop-count ${location.drop_locations?.length >= 3 ? 'sufficient' : 'insufficient'}`}>
                        {location.drop_locations?.length || 0}
                      </span>
                    </td>
                    <td>
                      <span className={`status-badge ${location.is_active ? 'active' : 'inactive'}`}>
                        {location.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="actions-cell">
                      <button
                        className="action-btn view-btn"
                        onClick={() => navigate(`/admin/locations/${location.id}`)}
                        title="View Details"
                      >
                        👁️
                      </button>
                      <button
                        className="action-btn edit-btn"
                        onClick={() => navigate(`/admin/locations/${location.id}/edit`)}
                        title="Edit"
                      >
                        ✏️
                      </button>
                      <button
                        className="action-btn toggle-btn"
                        onClick={() => handleToggleActive(location.id)}
                        title={location.is_active ? 'Deactivate' : 'Activate'}
                      >
                        {location.is_active ? '⏸️' : '▶️'}
                      </button>
                      <button
                        className="action-btn delete-btn"
                        onClick={() => handleDelete(location.id, location.name)}
                        title="Delete"
                      >
                        🗑️
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
};

export default LocationManagement;
